/**
 * IVR (Interactive Voice Response) and Call Groups
 * - Property IVR list and details
 * - IVR general settings, greeting, routing, and call hours
 * - Call Groups management and related modals
 */
export const ivrEn = {
  ivr: {
    properties: {
      propertiesLabel: 'Properties',
      allPlaceholder: '[All]',
      citiesLabel: 'Cities',
      activeNumbersLabel: 'Active Numbers',
      noActiveNumbersTooltip: 'There are no call tracking numbers for this property. Contact your sales rep to purchase or transfer lines.',
      fields: {
        propertyStatus: 'Property Status',
        propertyStatusTooltip: 'Indicates whether IVR is available for this property.',
        ivrStatus: 'IVR Status',
        ivrStatusTooltip: 'Indicates whether the IVR is actively enabled and routing incoming calls. When disabled, call tracking numbers will follow their default forwarding rules.',
      },
    },
    details: {
      ivrDisabledTitle: 'IVR routing is disabled for this property.',
      ivrDisabledMessage: 'Call tracking numbers will follow their default forwarding rules. Contact support to activate IVR routing.',
      tabIvrDisabledMessage: `Calls will use each tracking number's default forwarding settings.`,
      allRoutesDisabledTitle: 'All IVR routes are disabled.',
      allRoutesDisabledMessage: 'Calls will follow each number\'s default forwarding rules. Enable at least one IVR to start routing calls.',
      noCallTrackingNumbersMessage: 'There are no call tracking numbers associated with this property. Contact your sales rep to purchase new lines or transfer existing ones.',
      propertyIVRsTitle: 'Property IVRs',
      propertyIVRsSubtitle: 'Set schedules and call flows for each IVR on this property.',
      fallbackSuffix: '(Fallback)',
      nameLabel: 'Name',
      statusHelpTooltip: 'Disable to bypass this IVR and follow default forwarding behaviour during call hours.',
      sequentialDialingTimeoutLabel: 'Sequential Dialing Timeout',
      sequentialDialingTimeoutHelp: 'How long each number in a call group will be dialed before moving to the next number.',
      secondsAppend: 'seconds',
      callHoursTitle: 'Call Hours',
      greetingRoutingIVRTitle: 'Greeting, Routing, and IVR',
      thisIVRDisabledTitle: 'This IVR is disabled.',
      fallbackIVRMessage: 'During its scheduled hours, all active numbers will follow the Fallback IVR settings.',
      disabledDefaultForwardingMessage: `During its scheduled hours, calls will use each tracking number's default forwarding settings.`,
      thisIVREnabledTitle: 'This IVR is enabled.',
      enabledMessage: `During its scheduled hours, calls will follow the IVR's routes and forwarding rules configured below.`,
      saveSettingsButtonText: 'Save Settings',
      settingsSavedToast: 'Settings saved!',
      dialTimeoutValidationMessage: 'Timeout must be between 8 and 15 seconds',
      fields: {
        callTrackingNumber: 'Call Tracking #',
        sources: 'Sources',
      },
    },
    generalSettings: {
      defaultsHeading: 'IVR Defaults',
      defaultsSubtitle: 'Applies to all IVRs on this property.',
      defaultLanguageLabel: 'Default Language',
      languageSelectionTooltip: 'Prompts callers to dial 9 to switch between English and French.',
      languageSelectionLabel: 'Language Selection',
    },
    greetingRouting: {
      messageTypeLabel: 'Greeting and Routing Message Type',
      messageTypeTooltip: `If set to recording and a recording isn't found, the greeting will fall back to text-to-speech.`,
      recordingOption: 'Recording',
      textToSpeechOption: 'Text to Speech',
      previewCallFlowButton: 'Preview Call Flow',
      callGreetingHeading: 'Call Greeting',
      callGreetingSubtitle: 'Played before the IVR menu begins.',
      routingRulesHeading: 'Routing Rules',
      routingRulesSubtitle: 'Configure the IVR menu options callers can press. Customize the route order, call group, fallback, prompt and connecting message for each route.',
      digitOrderWarning: 'The digit assigned to one or more routes has changed. Make sure the menu prompt text for each route is updated to reflect the new digit if needed.',
      skipMenuLabel: 'Skip Menu Selection',
      skipMenuHelpTooltip: 'Skip the menu prompt and connect callers directly to the active route after the greeting.',
      skipMenuAutoDisabledWarning: 'Skip Menu Selection was disabled because more than one route is active.',
      skipMenuDisabledTooltip: 'Only available when a single route is enabled.',
      routeStatusDisabledTooltip: 'At least one route must remain enabled.',
      routeNameLabel: 'Route Name',
      callGroupLabel: 'Call Group',
      ifNobodyAnswersLabel: 'If Nobody Answers:',
      ifAiAgentUnavailableLabel: 'If AI Agent Is Unavailable:',
      customNumberLabel: 'Custom Number',
      voicemailLastNumberWithPhone: 'Go to Voicemail on Last Number ({phone})',
      voicemailLastNumberNoGroup: 'Go to Voicemail on Last Number (No group selected)',
      forwardToPropertyNumber: 'Forward to Property Number ({phone})',
      forwardToCustomNumber: 'Forward to Custom Number',
      noCallGroupChosen: 'No call group chosen',
      routeFallbackName: 'Route',
      menuPromptRecordingLabel: 'Menu Prompt Recording',
      connectingMessageRecordingLabel: 'Connecting Message Recording',
      uploadingTitle: 'Uploading',
      greetingUploadingMessage: 'Greeting is being uploaded!',
      greetingUploadedMessage: 'Greeting uploaded!',
      recordingUploadedMessage: 'Recording uploaded!',
      fields: {
        digit: 'Digit',
        routeName: 'Route Name',
        callGroup: 'Call Group',
        enabled: 'Enabled',
        enabledTooltip: 'Toggle off to skip this route in the IVR menu.',
      },
    },
    audioTextInput: {
      greetingRecordingLabel: 'Greeting Recording ({lang})',
      greetingTextLabel: 'Greeting Text ({lang})',
    },
    routingLanguageInput: {
      menuPromptLabel: 'Menu Prompt',
      connectingMessageLabel: 'Connecting Message',
      recordingLabelWithLang: '{label} Recording ({lang})',
      textLabelWithLang: '{label} Text ({lang})',
      menuPromptRecordingTooltip: 'Plays when listing menu options. Fallbacks to text if the recording is empty.',
      connectingMessageRecordingTooltip: 'Plays after caller presses digit. Fallbacks to text if the recording is empty.',
      menuPromptTextTooltip: 'Plays when listing menu options.',
      connectingMessageTextTooltip: 'Plays after caller presses digit.',
    },
    callHours: {
      timezoneBannerMessage: 'Timezone is determined by the building address. All times shown are in {timezone}.',
      allDayHoursText: '12:00 AM - 11:59 PM',
      toSeparator: 'to',
      fallbackBannerMessage: 'These settings act as the fallback IVR, automatically handling all calls received outside of your other active IVR schedules.',
      days: {
        sunday: 'Sunday',
        monday: 'Monday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
      },
      fields: {
        day: 'Day',
        availability: 'Availability',
        hours: 'Hours',
      },
      availabilityOptions: {
        closed: 'Closed',
        allDay: 'All Day',
        custom: 'Custom',
      },
    },
    recordingSettings: {
      defaultTitle: 'Greeting Recording',
      browserNoAudioSupport: 'Your browser does not support audio',
      personalizedRecordingLabel: 'Personalized Recording',
      currentlyLabel: 'currently:',
      noExistingAudioMessage: 'No existing audio recording!',
      audioWillBeRemovedMessage: 'Audio will be removed when you save your changes.',
      undoButton: 'undo',
      setNewAudioMessageLabel: 'Set New Audio Message - select method below:',
      overwriteWarningText: 'If you have a current recording above, it will be overwritten when you hit "Save Settings".',
    },
    callFlowPreview: {
      title: 'Call Flow Preview',
      okayButton: 'Okay',
      intro: 'This is an example of what a caller will hear when they call this property.',
      greetingHeading: 'Greeting',
      menuOptionsHeading: 'Menu Options',
      connectingMessageHeading: 'Connecting Message',
      recordingDisclaimerHeading: 'Recording Disclaimer',
      callConnectedHeading: 'Call Connected',
      callConnectedMessage: 'The call group for the selected route will be dialed.',
      languageSwitchingEnabledText: 'Language switching is enabled, callers will be prompted to press 9 to switch languages.',
      languageSwitchingDisabledText: 'Language switching is disabled, callers will not be prompted to press 9 to switch languages.',
      skipMenuEnabledNote: 'Skip Menu Selection is enabled — callers won\'t be prompted to press any digit and will be connected directly to the active route.',
      skipMenuLanguageSwitchWarning: 'Language switching is enabled but will not occur since callers won\'t be presented with a menu prompt.',
      noGreetingTextSet: '[No greeting text set]',
      noMenuPromptSetFor: '[No menu prompt set for "{routeName}"]',
      noConnectingMessageSetFor: '[No connecting message set for "{routeName}"]',
      noEnabledRoutes: '[No enabled routes]',
      thisRouteFallback: 'this route',
      mandatoryDisclaimerNote: 'NOTE: Only the recording disclaimer message is mandatory. The other messages are recommended, but not required.',
    },
    callGroups: {
      title: 'Call Groups',
      searchPlaceholder: 'Search groups...',
      sidebarLabel: 'CALL GROUPS',
      memberCount: '{count} member | {count} members',
      noGroupsFound: 'No groups found',
      addGroupButton: 'Add Group',
      groupNameLabel: 'Group Name',
      groupNamePlaceholder: 'e.g., Wilshire Towers - Leasing Team',
      ivrRoutesUsingGroupHeading: 'IVR Routes Using This Group',
      noIVRRoutesMessage: 'No IVR routes are currently using this group.',
      phoneNumbersHeading: 'Phone Numbers',
      dragToReorderHint: 'Drag to reorder for Sequential dialing',
      noPhoneNumbersMessage: 'No phone numbers yet — add one to start routing calls',
      maxEntriesTooltip: 'Max 10 numbers per call group.',
      addPhoneNumberButton: 'Add phone number',
      noCallGroupsYetMessage: 'No Call Groups yet. Create a call group to start routing your calls.',
      saveSettingsButtonText: 'Save Settings',
      settingsSavedToast: 'Settings saved!',
      addCallGroupModalTitle: 'Add Call Group',
      editPhoneNumberModalTitle: 'Edit Phone Number',
      addPhoneNumberModalTitle: 'Add Phone Number',
      labelPlaceholder: 'e.g., John Smith',
      phoneNumberFieldLabel: 'Phone Number',
      phoneNumberPlaceholder: '555-123-4567',
      languagesRequiredLabel: 'Languages',
      selectCallGroupModalTitle: '{routeName} - Call Group',
      aiCallGroupFallbackBannerMessage: `The AI Voice Agent Call Group can't be selected while it's set as this route's fallback option. Remove it as the fallback option first to enable it here.`,
      aiCallGroupFallbackTooltip: 'Remove the AI Voice Agent fallback option first to enable this call group.',
      searchCallGroupPlaceholder: 'Search by call group name, phone number or phone name.',
      manageCallGroupsButton: 'Manage Call Groups',
      noCallGroupsFoundSearch: 'No call groups found matching your search.',
      noCallGroupsAvailable: 'No call groups available.',
      noEntriesConfigured: 'No entries configured',
      dismissButton: 'Dismiss',
      infoIntro: 'Call groups are sets of phone numbers (operators) that can be dialed to handle calls from your IVR.',
      howItWorksHeading: 'How call groups work',
      howItWorksLi1: 'A <strong>call group</strong> is a list of team members reachable by phone.',
      howItWorksLi2: 'Call groups are attached to <strong>IVR routes</strong> (for example, Leasing or Resident Services).',
      howItWorksLi3: 'When a caller selects that route, the system <strong>dials the call group</strong> to connect them.',
      dialingBehaviorHeading: 'Dialing behavior',
      dialingBehaviorLi1: 'Operators are dialed in the <strong>order shown</strong> and you can <strong>drag & drop</strong> to reorder them.',
      dialingBehaviorLi2: 'If an operator is <strong>turned off</strong>, they are <strong>skipped</strong> during dialing.',
      whereUsedHeading: `Where they're used`,
      whereUsedLi1: 'Call groups can be <strong>shared across properties</strong>.',
      whereUsedLi2: 'If a group is only <strong>used by a single property</strong>, add the <strong>property name</strong> to the group to keep organized.',
      whereUsedLi3: '<strong>IVR Routes Using This Group</strong> — This list shows everywhere this group is currently assigned.',
      whereUsedLi4: '<strong>Updating a group will update it for all IVRs that use it.</strong>',
      routesFields: {
        route: 'Route',
      },
      entriesFields: {
        phoneNumber: 'Phone Number',
        languages: 'Languages',
        languagesTooltip: 'Languages this operator can answer in. For organization only; does not affect routing.',
        enabled: 'Enabled',
        enabledTooltip: 'Toggle off to skip this operator during dialing.',
      },
    },
  },
} as const;

export const ivrFr = {
  ivr: {
    properties: {
      propertiesLabel: 'Propriétés',
      allPlaceholder: '[Tous]',
      citiesLabel: 'Villes',
      activeNumbersLabel: 'Numéros actifs',
      noActiveNumbersTooltip: 'Il n\'y a aucun numéro de suivi des appels pour cette propriété. Contactez votre représentant pour acheter ou transférer des lignes.',
      fields: {
        propertyStatus: 'Statut de la propriété',
        propertyStatusTooltip: 'Indique si le RVI est disponible pour cette propriété.',
        ivrStatus: 'Statut du RVI',
        ivrStatusTooltip: 'Indique si le RVI est activement activé et achemine les appels entrants. Lorsqu\'il est désactivé, les numéros de suivi des appels suivent leurs règles de transfert par défaut.',
      },
    },
    details: {
      ivrDisabledTitle: 'Le routage RVI est désactivé pour cette propriété.',
      ivrDisabledMessage: 'Les numéros de suivi des appels suivront leurs règles de transfert par défaut. Contactez le soutien pour activer le routage RVI.',
      tabIvrDisabledMessage: 'Les appels utiliseront les paramètres de transfert par défaut de chaque numéro de suivi.',
      allRoutesDisabledTitle: 'Toutes les routes RVI sont désactivées.',
      allRoutesDisabledMessage: 'Les appels suivront les règles de transfert par défaut de chaque numéro. Activez au moins un RVI pour commencer à acheminer les appels.',
      noCallTrackingNumbersMessage: 'Aucun numéro de suivi des appels n\'est associé à cette propriété. Contactez votre représentant pour acheter de nouvelles lignes ou transférer des lignes existantes.',
      propertyIVRsTitle: 'RVI de la propriété',
      propertyIVRsSubtitle: 'Définissez les horaires et les flux d\'appels pour chaque RVI de cette propriété.',
      fallbackSuffix: '(Solution de repli)',
      nameLabel: 'Nom',
      statusHelpTooltip: 'Désactivez pour contourner ce RVI et suivre le comportement de transfert par défaut pendant les heures d\'appel.',
      sequentialDialingTimeoutLabel: 'Délai de numérotation séquentielle',
      sequentialDialingTimeoutHelp: 'Durée pendant laquelle chaque numéro d\'un groupe d\'appels sera composé avant de passer au numéro suivant.',
      secondsAppend: 'secondes',
      callHoursTitle: 'Heures d\'appel',
      greetingRoutingIVRTitle: 'Accueil, routage et RVI',
      thisIVRDisabledTitle: 'Ce RVI est désactivé.',
      fallbackIVRMessage: 'Pendant ses heures prévues, tous les numéros actifs suivront les paramètres du RVI de solution de repli.',
      disabledDefaultForwardingMessage: 'Pendant ses heures prévues, les appels utiliseront les paramètres de transfert par défaut de chaque numéro de suivi.',
      thisIVREnabledTitle: 'Ce RVI est activé.',
      enabledMessage: 'Pendant ses heures prévues, les appels suivront les routes et les règles de transfert du RVI configurées ci-dessous.',
      saveSettingsButtonText: 'Enregistrer les paramètres',
      settingsSavedToast: 'Paramètres enregistrés !',
      dialTimeoutValidationMessage: 'Le délai doit être compris entre 8 et 15 secondes',
      fields: {
        callTrackingNumber: 'N° de suivi des appels',
        sources: 'Sources',
      },
    },
    generalSettings: {
      defaultsHeading: 'Paramètres RVI par défaut',
      defaultsSubtitle: 'S\'applique à tous les RVI de cette propriété.',
      defaultLanguageLabel: 'Langue par défaut',
      languageSelectionTooltip: 'Invite les appelants à composer le 9 pour basculer entre l\'anglais et le français.',
      languageSelectionLabel: 'Sélection de la langue',
    },
    greetingRouting: {
      messageTypeLabel: 'Type de message d\'accueil et de routage',
      messageTypeTooltip: 'Si réglé sur enregistrement et qu\'aucun enregistrement n\'est trouvé, le message d\'accueil basculera vers la synthèse vocale.',
      recordingOption: 'Enregistrement',
      textToSpeechOption: 'Synthèse vocale',
      previewCallFlowButton: 'Aperçu du flux d\'appel',
      callGreetingHeading: 'Message d\'accueil',
      callGreetingSubtitle: 'Diffusé avant le début du menu RVI.',
      routingRulesHeading: 'Règles de routage',
      routingRulesSubtitle: 'Configurez les options du menu RVI que les appelants peuvent sélectionner. Personnalisez l\'ordre des routes, le groupe d\'appels, la solution de repli, l\'invite et le message de connexion pour chaque route.',
      digitOrderWarning: 'Le chiffre attribué à une ou plusieurs routes a changé. Assurez-vous que le texte de l\'invite du menu de chaque route est mis à jour pour refléter le nouveau chiffre, si nécessaire.',
      skipMenuLabel: 'Ignorer la sélection du menu',
      skipMenuHelpTooltip: 'Ignorer l\'invite du menu et connecter directement les appelants à la route active après le message d\'accueil.',
      skipMenuAutoDisabledWarning: 'Ignorer la sélection du menu a été désactivé car plus d\'une route est active.',
      skipMenuDisabledTooltip: 'Disponible uniquement lorsqu\'une seule route est activée.',
      routeStatusDisabledTooltip: 'Au moins une route doit rester activée.',
      routeNameLabel: 'Nom de la route',
      callGroupLabel: 'Groupe d\'appels',
      ifNobodyAnswersLabel: 'Si personne ne répond :',
      ifAiAgentUnavailableLabel: 'Si l\'agent IA n\'est pas disponible :',
      customNumberLabel: 'Numéro personnalisé',
      voicemailLastNumberWithPhone: 'Aller à la messagerie vocale sur le dernier numéro ({phone})',
      voicemailLastNumberNoGroup: 'Aller à la messagerie vocale sur le dernier numéro (Aucun groupe sélectionné)',
      forwardToPropertyNumber: 'Transférer au numéro de la propriété ({phone})',
      forwardToCustomNumber: 'Transférer à un numéro personnalisé',
      noCallGroupChosen: 'Aucun groupe d\'appels sélectionné',
      routeFallbackName: 'Route',
      menuPromptRecordingLabel: 'Enregistrement de l\'invite du menu',
      connectingMessageRecordingLabel: 'Enregistrement du message de connexion',
      uploadingTitle: 'Téléversement',
      greetingUploadingMessage: 'Le message d\'accueil est en cours de téléversement !',
      greetingUploadedMessage: 'Message d\'accueil téléversé !',
      recordingUploadedMessage: 'Enregistrement téléversé !',
      fields: {
        digit: 'Chiffre',
        routeName: 'Nom de la route',
        callGroup: 'Groupe d\'appels',
        enabled: 'Activé',
        enabledTooltip: 'Désactivez pour ignorer cette route dans le menu RVI.',
      },
    },
    audioTextInput: {
      greetingRecordingLabel: 'Enregistrement du message d\'accueil ({lang})',
      greetingTextLabel: 'Texte du message d\'accueil ({lang})',
    },
    routingLanguageInput: {
      menuPromptLabel: 'Invite du menu',
      connectingMessageLabel: 'Message de connexion',
      recordingLabelWithLang: 'Enregistrement de {label} ({lang})',
      textLabelWithLang: 'Texte de {label} ({lang})',
      menuPromptRecordingTooltip: 'Joué lors de l\'énumération des options du menu. Bascule vers le texte si l\'enregistrement est vide.',
      connectingMessageRecordingTooltip: 'Joué après que l\'appelant appuie sur un chiffre. Bascule vers le texte si l\'enregistrement est vide.',
      menuPromptTextTooltip: 'Joué lors de l\'énumération des options du menu.',
      connectingMessageTextTooltip: 'Joué après que l\'appelant appuie sur un chiffre.',
    },
    callHours: {
      timezoneBannerMessage: 'Le fuseau horaire est déterminé par l\'adresse de la propriété. Toutes les heures affichées sont en {timezone}.',
      allDayHoursText: '00 h 00 - 23 h 59',
      toSeparator: 'à',
      fallbackBannerMessage: 'Ces paramètres agissent comme le RVI de solution de repli, gérant automatiquement tous les appels reçus en dehors de vos autres horaires RVI actifs.',
      days: {
        sunday: 'Dimanche',
        monday: 'Lundi',
        tuesday: 'Mardi',
        wednesday: 'Mercredi',
        thursday: 'Jeudi',
        friday: 'Vendredi',
        saturday: 'Samedi',
      },
      fields: {
        day: 'Jour',
        availability: 'Disponibilité',
        hours: 'Heures',
      },
      availabilityOptions: {
        closed: 'Fermé',
        allDay: 'Toute la journée',
        custom: 'Personnalisé',
      },
    },
    recordingSettings: {
      defaultTitle: 'Enregistrement du message d\'accueil',
      browserNoAudioSupport: 'Votre navigateur ne prend pas en charge l\'audio',
      personalizedRecordingLabel: 'Enregistrement personnalisé',
      currentlyLabel: 'actuellement :',
      noExistingAudioMessage: 'Aucun enregistrement audio existant !',
      audioWillBeRemovedMessage: 'L\'audio sera supprimé lorsque vous enregistrerez vos modifications.',
      undoButton: 'annuler',
      setNewAudioMessageLabel: 'Définir un nouveau message audio - sélectionnez une méthode ci-dessous :',
      overwriteWarningText: 'Si vous avez un enregistrement actuel ci-dessus, il sera remplacé lorsque vous cliquerez sur « Enregistrer les paramètres ».',
    },
    callFlowPreview: {
      title: 'Aperçu du flux d\'appel',
      okayButton: 'D\'accord',
      intro: 'Voici un exemple de ce qu\'un appelant entendra en appelant cette propriété.',
      greetingHeading: 'Accueil',
      menuOptionsHeading: 'Options du menu',
      connectingMessageHeading: 'Message de connexion',
      recordingDisclaimerHeading: 'Avis d\'enregistrement',
      callConnectedHeading: 'Appel connecté',
      callConnectedMessage: 'Le groupe d\'appels de la route sélectionnée sera composé.',
      languageSwitchingEnabledText: 'Le changement de langue est activé, les appelants seront invités à appuyer sur 9 pour changer de langue.',
      languageSwitchingDisabledText: 'Le changement de langue est désactivé, les appelants ne seront pas invités à appuyer sur 9 pour changer de langue.',
      skipMenuEnabledNote: 'Ignorer la sélection du menu est activé — les appelants ne seront pas invités à appuyer sur un chiffre et seront connectés directement à la route active.',
      skipMenuLanguageSwitchWarning: 'Le changement de langue est activé mais ne se produira pas, car les appelants ne seront pas présentés avec une invite de menu.',
      noGreetingTextSet: '[Aucun texte de message d\'accueil défini]',
      noMenuPromptSetFor: '[Aucune invite de menu définie pour « {routeName} »]',
      noConnectingMessageSetFor: '[Aucun message de connexion défini pour « {routeName} »]',
      noEnabledRoutes: '[Aucune route activée]',
      thisRouteFallback: 'cette route',
      mandatoryDisclaimerNote: 'REMARQUE : Seul le message d\'avis d\'enregistrement est obligatoire. Les autres messages sont recommandés, mais non requis.',
    },
    callGroups: {
      title: 'Groupes d\'appels',
      searchPlaceholder: 'Rechercher des groupes...',
      sidebarLabel: 'GROUPES D\'APPELS',
      memberCount: '{count} membre | {count} membres',
      noGroupsFound: 'Aucun groupe trouvé',
      addGroupButton: 'Ajouter un groupe',
      groupNameLabel: 'Nom du groupe',
      groupNamePlaceholder: 'p. ex., Tours Wilshire - Équipe de location',
      ivrRoutesUsingGroupHeading: 'Routes RVI utilisant ce groupe',
      noIVRRoutesMessage: 'Aucune route RVI n\'utilise actuellement ce groupe.',
      phoneNumbersHeading: 'Numéros de téléphone',
      dragToReorderHint: 'Faites glisser pour réorganiser la numérotation séquentielle',
      noPhoneNumbersMessage: 'Aucun numéro de téléphone pour l\'instant — ajoutez-en un pour commencer à acheminer les appels',
      maxEntriesTooltip: 'Maximum 10 numéros par groupe d\'appels.',
      addPhoneNumberButton: 'Ajouter un numéro de téléphone',
      noCallGroupsYetMessage: 'Aucun groupe d\'appels pour l\'instant. Créez un groupe d\'appels pour commencer à acheminer vos appels.',
      saveSettingsButtonText: 'Enregistrer les paramètres',
      settingsSavedToast: 'Paramètres enregistrés !',
      addCallGroupModalTitle: 'Ajouter un groupe d\'appels',
      editPhoneNumberModalTitle: 'Modifier le numéro de téléphone',
      addPhoneNumberModalTitle: 'Ajouter un numéro de téléphone',
      labelPlaceholder: 'p. ex., John Smith',
      phoneNumberFieldLabel: 'Numéro de téléphone',
      phoneNumberPlaceholder: '555-123-4567',
      languagesRequiredLabel: 'Langues',
      selectCallGroupModalTitle: '{routeName} - Groupe d\'appels',
      aiCallGroupFallbackBannerMessage: 'Le groupe d\'appels de l\'agent vocal IA ne peut pas être sélectionné tant qu\'il est défini comme option de repli de cette route. Retirez-le d\'abord comme option de repli pour l\'activer ici.',
      aiCallGroupFallbackTooltip: 'Retirez d\'abord l\'option de repli de l\'agent vocal IA pour activer ce groupe d\'appels.',
      searchCallGroupPlaceholder: 'Rechercher par nom de groupe d\'appels, numéro de téléphone ou nom de téléphone.',
      manageCallGroupsButton: 'Gérer les groupes d\'appels',
      noCallGroupsFoundSearch: 'Aucun groupe d\'appels ne correspond à votre recherche.',
      noCallGroupsAvailable: 'Aucun groupe d\'appels disponible.',
      noEntriesConfigured: 'Aucune entrée configurée',
      dismissButton: 'Ignorer',
      infoIntro: 'Les groupes d\'appels sont des ensembles de numéros de téléphone (opérateurs) qui peuvent être composés pour gérer les appels de votre RVI.',
      howItWorksHeading: 'Comment fonctionnent les groupes d\'appels',
      howItWorksLi1: 'Un <strong>groupe d\'appels</strong> est une liste de membres de l\'équipe joignables par téléphone.',
      howItWorksLi2: 'Les groupes d\'appels sont associés à des <strong>routes RVI</strong> (par exemple, Location ou Services aux résidents).',
      howItWorksLi3: 'Lorsqu\'un appelant sélectionne cette route, le système <strong>compose le groupe d\'appels</strong> pour le connecter.',
      dialingBehaviorHeading: 'Comportement de numérotation',
      dialingBehaviorLi1: 'Les opérateurs sont composés dans <strong>l\'ordre affiché</strong> et vous pouvez les réorganiser par <strong>glisser-déposer</strong>.',
      dialingBehaviorLi2: 'Si un opérateur est <strong>désactivé</strong>, il est <strong>ignoré</strong> lors de la numérotation.',
      whereUsedHeading: 'Où ils sont utilisés',
      whereUsedLi1: 'Les groupes d\'appels peuvent être <strong>partagés entre les propriétés</strong>.',
      whereUsedLi2: 'Si un groupe n\'est <strong>utilisé que par une seule propriété</strong>, ajoutez le <strong>nom de la propriété</strong> au groupe pour rester organisé.',
      whereUsedLi3: '<strong>Routes RVI utilisant ce groupe</strong> — Cette liste montre partout où ce groupe est actuellement assigné.',
      whereUsedLi4: '<strong>La mise à jour d\'un groupe la mettra à jour pour tous les RVI qui l\'utilisent.</strong>',
      routesFields: {
        route: 'Route',
      },
      entriesFields: {
        phoneNumber: 'Numéro de téléphone',
        languages: 'Langues',
        languagesTooltip: 'Langues dans lesquelles cet opérateur peut répondre. À titre organisationnel seulement; n\'affecte pas le routage.',
        enabled: 'Activé',
        enabledTooltip: 'Désactivez pour ignorer cet opérateur lors de la numérotation.',
      },
    },
  },
} as const;
